import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { ProjectsContext } from "../../context/projectsContext";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import { useTheme } from "../../context/ThemeContext";

import "./works.css";

const categories = ["all work", "graphic design", "motion graphics", "3D"];
const projectsPerPage = 7;

const Works = () => {
  const { projects } = useContext(ProjectsContext);
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hoveredProject, setHoveredProject] = useState(null);
  const tooltipRef = useRef(null);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Normalize categories
  const normalizedProjects = useMemo(
    () =>
      projects.map((project) => ({
        ...project,
        category: Array.isArray(project.category)
          ? project.category
          : [project.category],
      })),
    [projects]
  );

  // Filter + sort
  const filteredAndSortedProjects = useMemo(() => {
    return normalizedProjects
      .filter((project) =>
        activeCategory === "all work"
          ? true
          : project.category.includes(activeCategory)
      )
      .sort((a, b) => a.index - b.index);
  }, [normalizedProjects, activeCategory]);

  // Pagination
  const currentProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * projectsPerPage;
    return filteredAndSortedProjects.slice(
      startIndex,
      startIndex + projectsPerPage
    );
  }, [filteredAndSortedProjects, currentPage]);

  const pageCount = Math.ceil(
    filteredAndSortedProjects.length / projectsPerPage
  );

  // Tooltip follows mouse
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!hoveredProject) return;
      const x = Math.min(e.clientX + 15, window.innerWidth - 310);
      const y = Math.min(e.clientY + 15, window.innerHeight - 10);
      setTooltipPosition({ x, y });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [hoveredProject]);

  const handleProjectClick = (project) => {
    const projectName = project.name.replace(/\s+/g, "-").toLowerCase();
    navigate(`/project/${projectName}`);
  };

  const { ref: headingRef, inView: headingInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="works" className="container works">
      <div className="row">
        {/* Heading */}
        <div className="col-12 d-flex justify-content-start">
          <h1
            ref={headingRef}
            className={`${
              headingInView || window.innerWidth <= 768
                ? "slide-in-left"
                : "slide-in-left-hidden"
            }`}
          >
            Work
          </h1>
        </div>

        {/* Categories */}
        <div className="col-12 mt-3">
          <ul className="d-flex justify-content-start gap-3 gap-md-5 flex-wrap categories-container">
            {categories.map((category) => (
              <li
                key={category}
                className={`category-item ${
                  category === activeCategory ? "active" : ""
                }`}
                onClick={() => {
                  setActiveCategory(category);
                  setCurrentPage(1);
                }}
              >
                <span>{category}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Projects */}
        <div className="col-12 mt-4">
          <ul className="project-list">
            {currentProjects.map((project) => (
              <li
                key={project.id}
                className="project-item"
                onClick={() => handleProjectClick(project)}
                onMouseEnter={(e) => {
                  setHoveredProject(project);
                  setTooltipPosition({ x: e.clientX + 15, y: e.clientY + 15 });
                }}
                onMouseLeave={() => setHoveredProject(null)}
              >
                <span>{project.name}</span>
                <span className="project-categories">
                  {project.category.join(", ")}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Tooltip */}
        {hoveredProject?.gallery?.length > 0 && (
          <div
            ref={tooltipRef}
            className="project-tooltip show"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              transform: `translate3d(${tooltipPosition.x}px, ${tooltipPosition.y}px, 0)`,
            }}
          >
            <img
              src={hoveredProject.gallery[0]}
              alt={hoveredProject.name}
              className="tooltip-image"
            />
          </div>
        )}

        {/* Pagination */}
        <div className="col-12 mt-5 d-flex justify-content-center">
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  color: isDark ? "#fff" : "rgba(0,0,0,0.87)",
                  bgcolor: isDark ? "#333" : "transparent",
                  border: isDark ? "1px solid #555" : "1px solid #ccc",
                  "&.Mui-selected": {
                    bgcolor: isDark ? "#555" : "gray",
                    color: isDark ? "#fff" : "#fff",
                  },
                  "&:hover": {
                    bgcolor: isDark ? "#444" : "#e0e0e0",
                  },
                }}
              />
            )}
          />
        </div>
      </div>
    </section>
  );
};

export default Works;
